var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../../services/api-client';
let JvAdminAgents = class JvAdminAgents extends LitElement {
    constructor() {
        super(...arguments);
        this._agents = [];
        this._sites = [];
        this._loading = true;
        this._showForm = false;
        this._formName = '';
        this._formSiteId = '';
        this._formError = '';
        this._loadError = '';
        this._saving = false;
    }
    static { this.styles = css `
    :host { display: block; }
    .dot-green { width: 8px; height: 8px; border-radius: 50%; background: #4C9A6A; display: inline-block; margin-right: 6px; }
    .dot-gray { width: 8px; height: 8px; border-radius: 50%; background: #7A7167; display: inline-block; margin-right: 6px; }
  `; }
    connectedCallback() {
        super.connectedCallback();
        void this._load();
    }
    async _load() {
        this._loading = true;
        this._loadError = '';
        try {
            const [agents, sites] = await Promise.all([
                api.get('/api/agents'),
                api.get('/api/sites'),
            ]);
            this._agents = agents;
            this._sites = sites;
        }
        catch (err) {
            this._loadError = err?.message ?? 'Unable to load agents. Try again.';
        }
        finally {
            this._loading = false;
        }
    }
    async _handleEnroll(e) {
        e.preventDefault();
        if (!this._formName || !this._formSiteId) {
            this._formError = 'Name and site are required.';
            return;
        }
        this._saving = true;
        this._formError = '';
        try {
            await api.post('/api/agents/enroll', { name: this._formName, siteId: this._formSiteId });
            this._showForm = false;
            this._formName = '';
            this._formSiteId = '';
            await this._load();
        }
        catch (err) {
            this._formError = err?.message ?? 'Failed to enroll agent.';
        }
        finally {
            this._saving = false;
        }
    }
    async _decommission(id, name) {
        if (!confirm(`Decommission agent "${name}"?`))
            return;
        try {
            await api.delete(`/api/agents/${id}`);
            this._agents = this._agents.filter(a => a.id !== id);
        }
        catch (err) {
            this._formError = err?.message ?? 'Failed to decommission agent.';
        }
    }
    _back() {
        this.dispatchEvent(new CustomEvent('admin-back', { bubbles: true, composed: true }));
    }
    render() {
        return html `
      <div class="max-w-content mx-auto px-5 sm:px-8 py-7">
        <div class="flex items-center gap-4 mb-6">
          <button @click=${this._back} class="text-sm text-jv-primary font-semibold hover:underline">&larr; Dashboard</button>
          <h1 class="text-2xl font-extrabold text-jv-ink">Edge Agents</h1>
          <button @click=${() => this._showForm = !this._showForm}
            class="ml-auto jv-btn-primary text-xs px-4 py-2">
            ${this._showForm ? 'Cancel' : '+ Enroll Agent'}
          </button>
        </div>

        ${this._formError ? html `<p class="text-jv-danger text-sm mb-4" role="alert">${this._formError}</p>` : ''}
        ${this._loadError ? html `<div class="jv-card p-6 mb-5" role="alert"><p class="text-jv-danger text-sm">${this._loadError}</p><button @click=${this._load} class="jv-btn-primary text-xs mt-4">Retry</button></div>` : ''}

        ${this._showForm ? html `
          <div class="jv-card mb-6"><div class="p-5">
            <h2 class="font-bold text-jv-ink mb-4">Enroll New Agent</h2>
            ${this._formError ? html `<p class="text-jv-danger text-sm mb-4">${this._formError}</p>` : ''}
            <form @submit=${this._handleEnroll}>
              <div class="mb-4"><label class="jv-label">Agent Name</label><input class="jv-input" .value=${this._formName} @input=${(e) => this._formName = e.target.value} placeholder="Classroom Pi 1" /></div>
              <div class="mb-4"><label class="jv-label">Site</label>
                <select class="jv-input" @change=${(e) => this._formSiteId = e.target.value}>
                  <option value="">Select site…</option>
                  ${this._sites.map(s => html `<option value=${s.id}>${s.name}</option>`)}
                </select>
              </div>
              <button type="submit" ?disabled=${this._saving} class="jv-btn-primary">${this._saving ? 'Enrolling…' : 'Enroll Agent'}</button>
            </form>
          </div></div>
        ` : ''}

        ${this._loading ? html `<p class="text-jv-ink-2 text-sm">Loading…</p>` : html `
          ${this._agents.length === 0 ? html `
            <div class="jv-card"><div class="p-10 text-center"><p class="font-semibold text-jv-ink">No agents enrolled</p><p class="text-sm text-jv-ink-2 mt-1">Enroll an edge agent to connect cameras.</p></div></div>
          ` : html `
            <div class="grid gap-4">
              ${this._agents.map(a => html `
                <div class="jv-card p-4 flex flex-wrap items-center gap-4">
                  <span class=${a.status === 'online' ? 'dot-green' : 'dot-gray'}></span>
                  <div class="flex-1 min-w-0">
                    <div class="font-bold text-sm text-jv-ink">${a.name}</div>
                    <div class="text-xs text-jv-ink-2">${a.siteName}${a.platform ? ` · ${a.platform}` : ''}${a.version ? ` v${a.version}` : ''}</div>
                  </div>
                  <div class="text-xs text-jv-ink-2">
                    ${a.lastSeenAt ? `Last seen: ${new Date(a.lastSeenAt).toLocaleString()}` : 'Never seen'}
                  </div>
                  <button @click=${() => this._decommission(a.id, a.name)}
                    class="text-xs text-jv-danger font-semibold hover:underline">Decommission</button>
                </div>
              `)}
            </div>
          `}
        `}
      </div>
    `;
    }
};
__decorate([
    state()
], JvAdminAgents.prototype, "_agents", void 0);
__decorate([
    state()
], JvAdminAgents.prototype, "_sites", void 0);
__decorate([
    state()
], JvAdminAgents.prototype, "_loading", void 0);
__decorate([
    state()
], JvAdminAgents.prototype, "_showForm", void 0);
__decorate([
    state()
], JvAdminAgents.prototype, "_formName", void 0);
__decorate([
    state()
], JvAdminAgents.prototype, "_formSiteId", void 0);
__decorate([
    state()
], JvAdminAgents.prototype, "_formError", void 0);
__decorate([
    state()
], JvAdminAgents.prototype, "_loadError", void 0);
__decorate([
    state()
], JvAdminAgents.prototype, "_saving", void 0);
JvAdminAgents = __decorate([
    customElement('jv-admin-agents')
], JvAdminAgents);
export { JvAdminAgents };
